/**
 * Qui tient chaque fonction de validation — réservé à l'administrateur.
 *
 * *Décision de Chris, 13/08/2026 : « un seul responsable par rôle » pour tout le groupe.*
 *
 * ## Pourquoi ici et pas au référentiel des ressources
 *
 * Les fonctions à nommer d'un chantier — conducteur, chef de chantier, chef d'équipe — se cochent
 * au référentiel MO depuis la 018, et c'était le bon choix : un code d'ERP écrit en dur serait un
 * fork qui commence.
 *
 * Ici c'est impossible, et la mesure le montre : au référentiel réel, le responsable exploitation
 * et un conducteur de travaux portent le **même code MO**. Aucune règle posée sur le code ne
 * saurait les distinguer — et ce n'est pas ce que le code décrit, il porte un coût horaire, pas
 * une responsabilité dans un circuit.
 *
 * ## Une fonction vacante se voit
 *
 * Sans titulaire, la demande du conducteur n'a aucun destinataire et le serveur la refuse. L'écran
 * le dit **avant** qu'il essaie, plutôt que de laisser un bouton qui ne marche pas.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { chargerResponsables, nommerResponsable, retirerResponsable } from "./api";
import type { Compte, FonctionTenue } from "./api";

export function Responsables({ membres }: { membres: readonly Compte[] }): JSX.Element {
  const [fonctions, setFonctions] = useState<readonly FonctionTenue[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  const recharger = (): void => {
    chargerResponsables()
      .then((r) => {
        setFonctions(r.fonctions);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  };

  useEffect(recharger, []);

  const agir = (promesse: Promise<unknown>, cle: string): void => {
    setOccupe(cle);
    setErreur(null);
    promesse
      .then(recharger)
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  // Un compte suspendu ne reçoit plus rien : le proposer ferait envoyer des demandes dans le vide.
  const nommables = membres.filter((m) => m.statut === "actif");

  return (
    <section className="referentiel">
      <h3>Responsables des validations</h3>
      <p className="etat">
        Un seul titulaire par fonction, pour tout le groupe. C'est à lui que partent les demandes
        de validation — sans titulaire, le conducteur ne peut pas soumettre.
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {fonctions === null ? (
        <p className="etat">Chargement…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Fonction</th>
              <th>Titulaire</th>
              <th>Nommer</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {fonctions.map((f) => (
              <tr key={f.fonction}>
                <td>{f.libelle}</td>
                <td>
                  {f.titulaire === null ? (
                    <span className="en-ecart">Personne — aucune demande ne peut partir</span>
                  ) : (
                    <>
                      {f.titulaire.prenom} {f.titulaire.nom}
                      {f.titulaire.statut !== "actif" && (
                        <span className="en-ecart"> · compte {f.titulaire.statut}</span>
                      )}
                    </>
                  )}
                </td>
                <td>
                  <select
                    value=""
                    disabled={occupe !== null}
                    onChange={(e) => {
                      if (e.target.value === "") return;
                      agir(nommerResponsable(f.fonction, e.target.value), f.fonction);
                    }}
                  >
                    <option value="">— choisir —</option>
                    {nommables.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.prenom} {m.nom}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {f.titulaire !== null && (
                    <button
                      type="button"
                      className="bouton-danger"
                      disabled={occupe !== null}
                      onClick={() => {
                        agir(retirerResponsable(f.fonction), f.fonction);
                      }}
                    >
                      Retirer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
